// Zoeken als overlay, zoals "Alles doorzoeken" in het huidige platform
// (SearchOverlay.tsx; owner 09-10: "search als layover zoals in de huidige
// frontend"): een compact zoekveld bovenaan opent bij een klik, typen of
// Ctrl K een laag over het hele scherm, met een donkere waas, een kaart
// bovenin en de resultaten per soort. Esc of een klik op de waas sluit.
// Een "pil" in het veld beperkt de vraag tot één skill ("Vraag de bronnen").

import { Search, X } from "lucide-react";
import { forwardRef, type KeyboardEvent, type ReactNode, useEffect, useRef } from "react";

import { Icoon } from "./basis";

/** Het compacte veld in de balk: één echt <input> (de lat telt één veld), dat
 *  alleen opent en zelf niets bewerkt. */
export const ZoekKnop = forwardRef<HTMLInputElement, { plaatshouder: string; onOpen: (begin?: string) => void }>(function ZoekKnop(
  { plaatshouder, onOpen },
  ref,
) {
  return (
    <label className="s-zoekveld s-zoekveld--knop">
      <Icoon als={Search} />
      <span className="s-verborgen">Zoeken</span>
      <input
        ref={ref}
        type="search"
        value=""
        data-lat="zoekveld"
        aria-haspopup="dialog"
        placeholder={plaatshouder}
        onClick={() => onOpen()}
        // Wat je typt of plakt, gaat meteen mee naar de overlay; dit veld blijft leeg.
        onChange={(e) => onOpen(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "ArrowDown") {
            e.preventDefault();
            onOpen();
          }
        }}
      />
      <kbd className="s-toets s-alleen-breed">Ctrl K</kbd>
    </label>
  );
});

export function ZoekOverlay({
  open,
  onSluit,
  waarde,
  onWaarde,
  plaatshouder,
  pil,
  onPilWeg,
  onEnter,
  children,
}: {
  open: boolean;
  onSluit: () => void;
  waarde: string;
  onWaarde: (v: string) => void;
  plaatshouder: string;
  /** De pil in het veld; zonder pil gaat de vraag over alles. */
  pil?: { label: string; uitleg?: string } | null;
  onPilWeg?: () => void;
  onEnter?: () => void;
  children: ReactNode;
}) {
  const veld = useRef<HTMLInputElement>(null);
  const kaart = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const vorige = document.activeElement as HTMLElement | null;
    requestAnimationFrame(() => veld.current?.focus());
    return () => {
      if (vorige && document.contains(vorige)) vorige.focus({ preventScroll: true });
    };
  }, [open]);
  if (!open) return null;

  /** Pijltjes lopen door de resultaten; Esc sluit. */
  const toets = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = [...(kaart.current?.querySelectorAll<HTMLElement>("[data-zoekitem]") ?? [])];
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onSluit();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      items[Math.min(items.length - 1, i + 1)]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (i <= 0) veld.current?.focus();
      else items[i - 1]?.focus();
    }
  };

  return (
    <div className="s-overlay" onMouseDown={(e) => e.target === e.currentTarget && onSluit()}>
      <div ref={kaart} className="s-zoekkaart" role="dialog" aria-modal="true" aria-label="Zoeken" data-lat="zweeft" onKeyDown={toets}>
        <div className="s-zoekkaart__regel">
          <Icoon als={Search} />
          {pil && (
            <span className="s-zoekpil" title={pil.uitleg}>
              <span className="s-afkap">{pil.label}</span>
              {onPilWeg && (
                <button type="button" className="s-zoekpil__weg" aria-label="Alleen deze skill: weghalen" onClick={onPilWeg}>
                  <Icoon als={X} klein />
                </button>
              )}
            </span>
          )}
          <input
            ref={veld}
            type="text"
            className="s-zoekkaart__veld"
            aria-label="Zoek of stel een vraag"
            placeholder={pil ? "Stel je vraag" : plaatshouder}
            value={waarde}
            onChange={(e) => onWaarde(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onEnter?.();
              } else if (e.key === "Backspace" && !waarde && pil && onPilWeg) onPilWeg();
            }}
          />
          <button type="button" className="s-toets s-zoekkaart__esc" onClick={onSluit}>
            esc
          </button>
        </div>
        <div className="s-zoekkaart__inhoud">{children}</div>
      </div>
    </div>
  );
}

/** Een kop met een groep regels in de overlay (skills, trends, bronnen, voorbeelden). */
export function ZoekGroep({ kop, children }: { kop: string; children: ReactNode }) {
  return (
    <div className="s-zoekgroep" role="group" aria-label={kop}>
      <div className="s-zoekgroep__kop">{kop}</div>
      {children}
    </div>
  );
}

/** Eén resultaat: soort, titel, meta en rechts eventueel een getal. */
export function ZoekItem({
  soort,
  children,
  meta,
  rechts,
  onKies,
  uit,
}: {
  soort?: string;
  children: ReactNode;
  meta?: ReactNode;
  rechts?: ReactNode;
  onKies?: () => void;
  /** Niet aan te klikken (bv. het scherm zit niet in de proef), met reden. */
  uit?: string;
}) {
  return (
    <button type="button" data-zoekitem className="s-zoekitem" disabled={!!uit} title={uit} onClick={onKies}>
      {soort && <span className="s-zoekitem__soort">{soort}</span>}
      <span className="s-zoekitem__tekst">
        <span className="s-zoekitem__titel s-afkap">{children}</span>
        {meta && <span className="s-zoekitem__meta s-afkap">{meta}</span>}
      </span>
      {rechts && <span className="s-zoekitem__rechts">{rechts}</span>}
    </button>
  );
}

/** Voorbeelden als kleine knoppen (zoals de tags in de huidige overlay). */
export function ZoekVoorbeelden({ kop, voorbeelden, onKies }: { kop: string; voorbeelden: readonly string[]; onKies: (v: string) => void }) {
  return (
    <div className="s-zoekgroep" role="group" aria-label={kop}>
      <div className="s-zoekgroep__kop">{kop}</div>
      <div className="s-zoekvoorbeelden">
        {voorbeelden.map((v) => (
          <button key={v} type="button" data-zoekitem className="s-chip" onClick={() => onKies(v)}>
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}
